import { useState, useEffect } from 'react';
import { Calendar, RotateCw } from 'lucide-react';

interface DateRangeFilterProps {
  start: string;
  end: string;
  onApply: (start: string, end: string) => void;
  disabled?: boolean;
}

export default function DateRangeFilter({
  start,
  end,
  onApply,
  disabled,
}: DateRangeFilterProps) {
  const [draftStart, setDraftStart] = useState(toLocalInput(start));
  const [draftEnd, setDraftEnd] = useState(toLocalInput(end));

  useEffect(() => {
    setDraftStart(toLocalInput(start));
    setDraftEnd(toLocalInput(end));
  }, [start, end]);

  const dirty =
    toLocalInput(start) !== draftStart || toLocalInput(end) !== draftEnd;

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border bg-card px-3 py-2.5">
      <Calendar className="h-4 w-4 text-muted-foreground self-center shrink-0" />
      <label className="flex flex-col text-xs text-muted-foreground">
        <span className="mb-0.5">From</span>
        <input
          type="datetime-local"
          value={draftStart}
          onChange={(e) => setDraftStart(e.target.value)}
          className="rounded border bg-background px-2 py-1 text-sm text-foreground"
          disabled={disabled}
        />
      </label>
      <label className="flex flex-col text-xs text-muted-foreground">
        <span className="mb-0.5">To</span>
        <input
          type="datetime-local"
          value={draftEnd}
          onChange={(e) => setDraftEnd(e.target.value)}
          className="rounded border bg-background px-2 py-1 text-sm text-foreground"
          disabled={disabled}
        />
      </label>
      <button
        type="button"
        onClick={() => {
          const s = fromLocalInput(draftStart);
          const e = fromLocalInput(draftEnd);
          if (s && e) onApply(s, e);
        }}
        disabled={disabled || !dirty || !draftStart || !draftEnd}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <RotateCw className="h-3.5 w-3.5" />
        Apply
      </button>
    </div>
  );
}

// RFC3339 (e.g. 2026-05-06T13:45:00Z) → datetime-local value (YYYY-MM-DDTHH:MM
// in the user's local zone). Returns '' for unparseable input.
function toLocalInput(rfc3339: string): string {
  if (!rfc3339) return '';
  const d = new Date(rfc3339);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function fromLocalInput(local: string): string {
  if (!local) return '';
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
}

export function defaultRange(days = 1): { start: string; end: string } {
  const end = new Date();
  const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000);
  return { start: start.toISOString(), end: end.toISOString() };
}
